import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Chinese Lunar Age & Zodiac Animal Calculator (2026 Year of the Horse)";
const DESC = "Get your traditional Chinese lunar age (nominal age), zodiac animal from the 12-year cycle and elemental sign from the 5-element cycle — plus 2026 fortune outlook.";

const FAQS = [
  { q: "How is Chinese lunar age different from Western age?", a: "In the traditional East Asian nominal system, you are 1 year old at birth and gain a year every Lunar New Year — so lunar age is typically 1 or 2 years higher than Western age." },
  { q: "What is my Chinese zodiac animal?", a: "Enter your DOB above — the tool maps your birth year to one of the 12 animals: Rat, Ox, Tiger, Rabbit, Dragon, Snake, Horse, Goat, Monkey, Rooster, Dog, Pig." },
  { q: "What element am I in the Chinese five-element cycle?", a: "The 5 elements — Wood, Fire, Earth, Metal, Water — cycle every 2 years within each 10-year 'stem' block, giving 60 unique animal-element combinations." },
  { q: "Which zodiac is 2026?", a: "2026 is the Year of the Horse (Fire Horse). It's traditionally seen as a year favouring bold career moves and long journeys." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "animals", label: "12 Animals" },
  { id: "elements", label: "5 Elements" },
  { id: "faqs", label: "FAQs" },
];

const ANIMALS = ["Monkey", "Rooster", "Dog", "Pig", "Rat", "Ox", "Tiger", "Rabbit", "Dragon", "Snake", "Horse", "Goat"];
const ANIMAL_EMOJI: Record<string, string> = { Rat: "🐀", Ox: "🐂", Tiger: "🐅", Rabbit: "🐇", Dragon: "🐉", Snake: "🐍", Horse: "🐎", Goat: "🐐", Monkey: "🐒", Rooster: "🐓", Dog: "🐕", Pig: "🐖" };
const ELEMENTS = ["Metal", "Water", "Wood", "Fire", "Earth"];

export const Route = createFileRoute("/age-calculator/chinese-lunar-age")({
  head: () => leafHead({
    path: "/age-calculator/chinese-lunar-age", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Chinese Lunar Age Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Chinese Lunar Age", url: "https://drhint.com/age-calculator/chinese-lunar-age" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const r = useMemo(() => (dob ? chronoDiff(dob, todayISO()) : null), [dob]);
  const details = useMemo(() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const animalIdx = year % 12;
    const elementIdx = Math.floor((year % 10) / 2);
    const animal = ANIMALS[animalIdx];
    const element = ELEMENTS[elementIdx];
    const western = r ? r.years : 0;
    const lunar = western + 2; // traditional East-Asian nominal age
    return { animal, element, western, lunar };
  }, [dob, r]);
  const copyText = details ? `Lunar age ${details.lunar} · Western ${details.western} · ${details.element} ${details.animal} ${ANIMAL_EMOJI[details.animal]}` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Chinese Lunar Age
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Traditional East-Asian <strong>nominal age</strong>, 12-animal zodiac aur 5-element cycle — sab ek jagah. Standard chronological age chahiye? Use the <Link to="/age-calculator" className="text-primary underline">core age calculator</Link>.
        </p>
      </section>
      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <TripleDateInput label="Your Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Reveal Zodiac & Lunar Age" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && details ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Western Age" value={`${details.western} yrs`} />
                  <ResultCard label="Chinese Lunar Age" value={`${details.lunar} yrs`} sub="1 at birth, +1 each Lunar NY" />
                  <ResultCard label="Zodiac Animal" value={`${ANIMAL_EMOJI[details.animal]} ${details.animal}`} />
                  <ResultCard label="Element" value={details.element} sub="5-element cycle" />
                </div>
                <div className="mt-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-card p-5">
                  <div className="text-sm">Your full sign: <strong>{details.element} {details.animal} {ANIMAL_EMOJI[details.animal]}</strong> — one of 60 unique combinations that repeat every 60 years. In 2026 (Fire Horse), your sign's compatibility, favourable colours and career sectors shift based on element interactions.</div>
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy Zodiac Card" /></div>
                <LifeProgressBar dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter your DOB to reveal your Chinese zodiac.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="animals" title="The 12 Zodiac Animals & Their Traits">
        <table>
          <thead><tr><th>Animal</th><th>Recent Years</th><th>Trait Snapshot</th></tr></thead>
          <tbody>
            <tr><td>🐀 Rat</td><td>1996, 2008, 2020</td><td>Sharp, resourceful</td></tr>
            <tr><td>🐂 Ox</td><td>1997, 2009, 2021</td><td>Patient, dependable</td></tr>
            <tr><td>🐅 Tiger</td><td>1998, 2010, 2022</td><td>Brave, competitive</td></tr>
            <tr><td>🐇 Rabbit</td><td>1999, 2011, 2023</td><td>Elegant, kind</td></tr>
            <tr><td>🐉 Dragon</td><td>2000, 2012, 2024</td><td>Powerful, lucky</td></tr>
            <tr><td>🐍 Snake</td><td>2001, 2013, 2025</td><td>Wise, enigmatic</td></tr>
            <tr><td>🐎 Horse</td><td>2002, 2014, 2026</td><td>Free-spirited, adventurous</td></tr>
            <tr><td>🐐 Goat</td><td>2003, 2015, 2027</td><td>Gentle, artistic</td></tr>
            <tr><td>🐒 Monkey</td><td>2004, 2016, 2028</td><td>Witty, curious</td></tr>
            <tr><td>🐓 Rooster</td><td>2005, 2017, 2029</td><td>Observant, honest</td></tr>
            <tr><td>🐕 Dog</td><td>2006, 2018, 2030</td><td>Loyal, protective</td></tr>
            <tr><td>🐖 Pig</td><td>2007, 2019, 2031</td><td>Generous, warm-hearted</td></tr>
          </tbody>
        </table>
      </AnchorSection>

      <AnchorSection id="elements" title="The 5-Element Cycle Explained">
        <p>Each 2-year block within a decade is governed by one of five elements — <strong>Wood, Fire, Earth, Metal, Water</strong>. The pairing of an element with an animal creates a 60-year 'stem-branch' cycle. Fire Horse (2026), for example, doubles the animal's fiery traits with an active-element year — traditionally seen as unusually intense.</p>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}